import { useState } from 'react';
import { lockScroll, unlockScroll } from '../utils/scrollLock';
import { toPastelColor as toPastelColorUtil } from '../utils/svgHelpers';

export function useLenses({ lenses, setLenses, boreholes, getSvgCoordinates, usePastelColors, outlineOnly, createBezierPath }) {
  // Stany dla soczewek geologicznych
  const [lensMode, setLensMode] = useState(false); // Tryb rysowania soczewki
  const [lensShape, setLensShape] = useState('circle'); // 'circle', 'rectangle', 'polygon'
  const [currentLens, setCurrentLens] = useState(null); // Aktualnie rysowana soczewka
  const [editingLens, setEditingLens] = useState(null); // ID edytowanej soczewki
  const [draggingLens, setDraggingLens] = useState(null); // Przeciągana soczewka
  const [resizingLens, setResizingLens] = useState(null); // Zmiana rozmiaru soczewki
  const [lensPolygonPoints, setLensPolygonPoints] = useState([]); // Punkty dla wielokąta
  const [lensEditMode, setLensEditMode] = useState(false); // Tryb edycji kształtu soczewek (splajny)
  const [lensMoveMode, setLensMoveMode] = useState(false); // Tryb przesuwania całych soczewek
  const [lensControlPoints, setLensControlPoints] = useState({}); // Punkty kontrolne dla soczewek { lensId-edge: {x, y} }
  const [draggingLensControl, setDraggingLensControl] = useState(null); // Przeciągany punkt kontrolny { lensId, edge }
  const [lensVertices, setLensVertices] = useState({}); // Przesunięte wierzchołki soczewek { lensId: {topLeft, topRight, bottomRight, bottomLeft} }
  const [draggingLensVertex, setDraggingLensVertex] = useState(null); // Przeciągany wierzchołek { lensId, vertex }

  // Wrapper na toPastelColor z uwzględnieniem stanu usePastelColors
  const toPastelColor = (hexColor) => toPastelColorUtil(hexColor, usePastelColors);

  // ===== FUNKCJE OBSŁUGI SOCZEWEK GEOLOGICZNYCH =====

  const handleLensStart = (e) => {
    if (!lensMode) return;

    const { x, y } = getSvgCoordinates(e);

    if (lensShape === 'polygon') {
      // Dla wielokąta - dodaj punkt
      setLensPolygonPoints(prev => [...prev, { x, y }]);
    } else {
      // Dla koła i prostokąta - rozpocznij rysowanie
      const newLens = {
        id: Date.now(),
        shape: lensShape,
        baseLayer: null, // Zostanie ustawione w dialogu
        name: '',
        color: '#8B4513',
        pattern: 'none'
      };

      if (lensShape === 'circle') {
        newLens.centerX = x;
        newLens.centerY = y;
        newLens.radius = 0;
      } else if (lensShape === 'rectangle') {
        newLens.x = x;
        newLens.y = y;
        newLens.width = 0;
        newLens.height = 0;
      }

      setCurrentLens(newLens);
    }
  };

  const handleLensMove = (e) => {
    if (!currentLens) return;

    const { x, y } = getSvgCoordinates(e);

    setCurrentLens(prev => {
      if (prev.shape === 'circle') {
        const dx = x - prev.centerX;
        const dy = y - prev.centerY;
        const radius = Math.sqrt(dx * dx + dy * dy);
        return { ...prev, radius };
      } else if (prev.shape === 'rectangle') {
        return {
          ...prev,
          width: x - prev.x,
          height: y - prev.y
        };
      }
      return prev;
    });
  };

  const handleLensEnd = () => {
    if (currentLens && lensShape !== 'polygon') {
      // Sprawdź czy ma rozmiar
      const hasSize = lensShape === 'circle'
        ? currentLens.radius > 5
        : (Math.abs(currentLens.width) > 5 && Math.abs(currentLens.height) > 5);

      if (hasSize) {
        // Otwórz dialog właściwości soczewki
        openLensPropertiesDialog(currentLens);
      }
      setCurrentLens(null);
    }
  };

  const handleLensPolygonComplete = () => {
    if (lensPolygonPoints.length >= 3) {
      const newLens = {
        id: Date.now(),
        shape: 'polygon',
        baseLayer: null,
        name: '',
        color: '#8B4513',
        pattern: 'none',
        points: [...lensPolygonPoints]
      };

      // Otwórz dialog właściwości
      openLensPropertiesDialog(newLens);
      setLensPolygonPoints([]);
    }
  };

  const openLensPropertiesDialog = (lens) => {
    // Znajdź wszystkie unikalne warstwy z otworów
    const uniqueLayers = new Set();
    boreholes.forEach(borehole => {
      borehole.layers.forEach(layer => {
        uniqueLayers.add(layer.name);
      });
    });

    // Znajdź pierwszą warstwę jako domyślną
    const defaultLayer = boreholes[0]?.layers[0];

    if (defaultLayer) {
      lens.baseLayer = defaultLayer.name;
      lens.name = defaultLayer.name;
      lens.color = defaultLayer.color;
      lens.pattern = defaultLayer.pattern;
    }

    setEditingLens(lens);
  };

  const saveLensProperties = (lensData) => {
    setLenses(prev => {
      // Sprawdź czy edytujemy istniejącą soczewkę
      const existingIndex = prev.findIndex(l => l.id === lensData.id);
      if (existingIndex !== -1) {
        // Edycja istniejącej soczewki
        const updated = [...prev];
        updated[existingIndex] = lensData;
        return updated;
      } else {
        // Dodanie nowej soczewki
        return [...prev, lensData];
      }
    });
    setEditingLens(null);
    // Wyłącz tryb rysowania soczewek po zapisaniu
    setLensMode(false);
  };

  const deleteLens = (lensId) => {
    setLenses(prev => prev.filter(l => l.id !== lensId));
  };

  const handleLensDoubleClick = (e, lens) => {
    // Zapobiegaj domyślnemu zachowaniu (zaznaczanie tekstu, scroll, etc.)
    e.preventDefault();
    e.stopPropagation();
    // Otwórz dialog edycji właściwości istniejącej soczewki
    setEditingLens({...lens});
  };

  // ===== FUNKCJA RENDEROWANIA SOCZEWKI =====
  const renderLens = (lens) => {
    const { shape, color, pattern, id } = lens;

    // Renderuj kształt w zależności od typu - używamy bezpośrednio współrzędnych SVG
    if (shape === 'circle') {
      const { centerX, centerY, radius } = lens;
      return (
        <circle
          cx={centerX}
          cy={centerY}
          r={radius}
          fill={outlineOnly ? "none" : (pattern !== 'none' ? `url(#pattern-${pattern})` : toPastelColor(color))}
          stroke={outlineOnly ? (color || '#000') : "none"}
          strokeWidth={outlineOnly ? "1.5" : 0}
        />
      );
    } else if (shape === 'rectangle') {
      const { x, y, width, height } = lens;
      const rectWidth = Math.abs(width);
      const rectHeight = Math.abs(height);
      const rectX = width < 0 ? x + width : x;
      const rectY = height < 0 ? y + height : y;

      // Jeśli tryb edycji, użyj krzywych Béziera
      if (lensEditMode) {
        // Punkty narożników - domyślne
        const defaultTopLeft = { x: rectX, y: rectY };
        const defaultTopRight = { x: rectX + rectWidth, y: rectY };
        const defaultBottomRight = { x: rectX + rectWidth, y: rectY + rectHeight };
        const defaultBottomLeft = { x: rectX, y: rectY + rectHeight };

        // Pobierz przesunięte wierzchołki lub użyj domyślnych
        const vertices = lensVertices[id] || {};
        const topLeft = vertices.topLeft || defaultTopLeft;
        const topRight = vertices.topRight || defaultTopRight;
        const bottomRight = vertices.bottomRight || defaultBottomRight;
        const bottomLeft = vertices.bottomLeft || defaultBottomLeft;

        // Domyślne punkty kontrolne (środki krawędzi)
        const defaultTop = { x: (topLeft.x + topRight.x) / 2, y: (topLeft.y + topRight.y) / 2 };
        const defaultRight = { x: (topRight.x + bottomRight.x) / 2, y: (topRight.y + bottomRight.y) / 2 };
        const defaultBottom = { x: (bottomRight.x + bottomLeft.x) / 2, y: (bottomRight.y + bottomLeft.y) / 2 };
        const defaultLeft = { x: (bottomLeft.x + topLeft.x) / 2, y: (bottomLeft.y + topLeft.y) / 2 };

        // Pobierz punkty kontrolne lub użyj domyślnych
        const cpTop = lensControlPoints[`${id}-top`] || defaultTop;
        const cpRight = lensControlPoints[`${id}-right`] || defaultRight;
        const cpBottom = lensControlPoints[`${id}-bottom`] || defaultBottom;
        const cpLeft = lensControlPoints[`${id}-left`] || defaultLeft;

        // Tworzenie ścieżki z krzywymi Béziera
        const pathTop = createBezierPath(topLeft.x, topLeft.y, topRight.x, topRight.y, cpTop);
        const pathRight = createBezierPath(topRight.x, topRight.y, bottomRight.x, bottomRight.y, cpRight);
        const pathBottom = createBezierPath(bottomRight.x, bottomRight.y, bottomLeft.x, bottomLeft.y, cpBottom);
        const pathLeft = createBezierPath(bottomLeft.x, bottomLeft.y, topLeft.x, topLeft.y, cpLeft);

        const fullPath = `${pathTop} ${pathRight.replace('M', 'L').replace(/M [0-9\.]+ [0-9\.]+/, '')} ${pathBottom.replace('M', 'L').replace(/M [0-9\.]+ [0-9\.]+/, '')} ${pathLeft.replace('M', 'L').replace(/M [0-9\.]+ [0-9\.]+/, '')} Z`;

        return (
          <path
            d={fullPath}
            fill={outlineOnly ? "none" : (pattern !== 'none' ? `url(#pattern-${pattern})` : toPastelColor(color))}
            stroke={outlineOnly ? (color || '#000') : "none"}
            strokeWidth={outlineOnly ? "1.5" : 0}
          />
        );
      }

      return (
        <rect
          x={rectX}
          y={rectY}
          width={rectWidth}
          height={rectHeight}
          fill={outlineOnly ? "none" : (pattern !== 'none' ? `url(#pattern-${pattern})` : toPastelColor(color))}
          stroke={outlineOnly ? (color || '#000') : "none"}
          strokeWidth={outlineOnly ? "1.5" : 0}
        />
      );
    } else if (shape === 'polygon' && lens.points && lens.points.length >= 3) {
      // W trybie edycji użyj niestandardowych wierzchołków jeśli istnieją
      let points = lens.points;
      if (lensEditMode && lensVertices[id]) {
        points = lens.points.map((p, idx) => {
          const customVertex = lensVertices[id][`vertex${idx}`];
          return customVertex || p;
        });
      }

      // W trybie edycji użyj krzywych Béziera
      if (lensEditMode) {
        const n = points.length;
        let pathD = `M ${points[0].x} ${points[0].y}`;

        // Dla każdej krawędzi wielokąta
        for (let i = 0; i < n; i++) {
          const startPoint = points[i];
          const endPoint = points[(i + 1) % n];

          // Domyślny punkt kontrolny (środek krawędzi)
          const defaultControlX = (startPoint.x + endPoint.x) / 2;
          const defaultControlY = (startPoint.y + endPoint.y) / 2;

          // Pobierz niestandardowy punkt kontrolny lub użyj domyślnego
          const controlKey = `${id}-edge${i}`;
          const controlPoint = lensControlPoints[controlKey] || { x: defaultControlX, y: defaultControlY };

          // Utwórz krzywą Béziera
          pathD += ` Q ${controlPoint.x} ${controlPoint.y} ${endPoint.x} ${endPoint.y}`;
        }

        pathD += ' Z';

        return (
          <path
            d={pathD}
            fill={outlineOnly ? "none" : (pattern !== 'none' ? `url(#pattern-${pattern})` : toPastelColor(color))}
            stroke={outlineOnly ? (color || '#000') : "none"}
            strokeWidth={outlineOnly ? "1.5" : 0}
          />
        );
      } else {
        // Normalny tryb - użyj prostych linii
        const pointsString = points.map(p => `${p.x},${p.y}`).join(' ');

        return (
          <polygon
            points={pointsString}
            fill={outlineOnly ? "none" : (pattern !== 'none' ? `url(#pattern-${pattern})` : toPastelColor(color))}
            stroke={outlineOnly ? (color || '#000') : "none"}
            strokeWidth={outlineOnly ? "1.5" : 0}
          />
        );
      }
    }

    return null;
  };

  // ===== FUNKCJE OBSŁUGI PUNKTÓW KONTROLNYCH SOCZEWEK =====
  const handleLensControlMouseDown = (e, lensId, edge, defaultX, defaultY) => {
    e.stopPropagation();
    e.preventDefault();

    // Zablokuj przewijanie strony
    lockScroll();

    setDraggingLensControl({ lensId, edge, defaultX, defaultY });
  };

  const handleLensControlMouseMove = (e) => {
    if (!draggingLensControl) return;
    e.preventDefault();
    const { x, y } = getSvgCoordinates(e);

    const key = `${draggingLensControl.lensId}-${draggingLensControl.edge}`;
    setLensControlPoints(prev => ({
      ...prev,
      [key]: { x, y }
    }));
  };

  const handleLensControlMouseUp = () => {
    // Przywróć przewijanie strony
    unlockScroll();

    setDraggingLensControl(null);
  };

  const resetLensControlPoint = (lensId, edge) => {
    const key = `${lensId}-${edge}`;
    setLensControlPoints(prev => {
      const newPoints = { ...prev };
      delete newPoints[key];
      return newPoints;
    });
  };

  // ===== FUNKCJE OBSŁUGI WIERZCHOŁKÓW SOCZEWEK =====
  const handleLensVertexMouseDown = (e, lensId, vertex) => {
    e.stopPropagation();
    e.preventDefault();

    // Zablokuj przewijanie strony
    lockScroll();

    setDraggingLensVertex({ lensId, vertex });
  };

  const handleLensVertexMouseMove = (e) => {
    if (!draggingLensVertex) return;
    e.preventDefault();
    const { x, y } = getSvgCoordinates(e);

    const { lensId, vertex } = draggingLensVertex;
    setLensVertices(prev => ({
      ...prev,
      [lensId]: {
        ...(prev[lensId] || {}),
        [vertex]: { x, y }
      }
    }));
  };

  const handleLensVertexMouseUp = () => {
    // Przywróć przewijanie strony
    unlockScroll();

    setDraggingLensVertex(null);
  };

  const resetLensVertex = (lensId, vertex) => {
    setLensVertices(prev => {
      const newVertices = { ...prev };
      if (newVertices[lensId]) {
        delete newVertices[lensId][vertex];
        if (Object.keys(newVertices[lensId]).length === 0) {
          delete newVertices[lensId];
        }
      }
      return newVertices;
    });
  };

  // ===== FUNKCJE OBSŁUGI PRZESUWANIA CAŁYCH SOCZEWEK =====
  const handleLensDragStart = (e, lensId) => {
    if (!lensMoveMode) return;
    e.stopPropagation();
    e.preventDefault();

    // Zablokuj przewijanie strony
    lockScroll();

    const { x, y } = getSvgCoordinates(e);
    const lens = lenses.find(l => l.id === lensId);

    if (!lens) return;

    // Zapisz pozycję początkową dla offsetu
    let offsetX, offsetY;

    if (lens.shape === 'circle') {
      offsetX = x - lens.centerX;
      offsetY = y - lens.centerY;
    } else if (lens.shape === 'rectangle') {
      offsetX = x - lens.x;
      offsetY = y - lens.y;
    } else if (lens.shape === 'polygon' && lens.points && lens.points.length > 0) {
      // Dla wielokąta użyj centroidu jako punktu odniesienia
      const sumX = lens.points.reduce((sum, p) => sum + p.x, 0);
      const sumY = lens.points.reduce((sum, p) => sum + p.y, 0);
      const centerX = sumX / lens.points.length;
      const centerY = sumY / lens.points.length;
      offsetX = x - centerX;
      offsetY = y - centerY;
    }

    setDraggingLens({ lensId, offsetX, offsetY });
  };

  const handleLensDragMove = (e) => {
    if (!draggingLens) return;
    e.preventDefault();

    const { x, y } = getSvgCoordinates(e);
    const { lensId, offsetX, offsetY } = draggingLens;

    setLenses(prevLenses => {
      return prevLenses.map(lens => {
        if (lens.id !== lensId) return lens;

        if (lens.shape === 'circle') {
          return {
            ...lens,
            centerX: x - offsetX,
            centerY: y - offsetY
          };
        } else if (lens.shape === 'rectangle') {
          return {
            ...lens,
            x: x - offsetX,
            y: y - offsetY
          };
        } else if (lens.shape === 'polygon' && lens.points) {
          // Oblicz obecne centrum
          const sumX = lens.points.reduce((sum, p) => sum + p.x, 0);
          const sumY = lens.points.reduce((sum, p) => sum + p.y, 0);
          const centerX = sumX / lens.points.length;
          const centerY = sumY / lens.points.length;

          // Oblicz nowe centrum
          const newCenterX = x - offsetX;
          const newCenterY = y - offsetY;

          // Przesuń wszystkie punkty
          const deltaX = newCenterX - centerX;
          const deltaY = newCenterY - centerY;

          const newPoints = lens.points.map(p => ({
            x: p.x + deltaX,
            y: p.y + deltaY
          }));

          return {
            ...lens,
            points: newPoints
          };
        }

        return lens;
      });
    });
  };

  const handleLensDragEnd = () => {
    // Przywróć przewijanie strony
    unlockScroll();

    setDraggingLens(null);
  };

  // Funkcja zapisująca zmodyfikowane wierzchołki do struktury soczewek
  const saveLensEdits = () => {
    setLenses(prevLenses => {
      return prevLenses.map(lens => {
        const lensId = lens.id;
        const modifiedVertices = lensVertices[lensId];

        // Jeśli wielokąt ma zmodyfikowane wierzchołki, zapisz je
        if (lens.shape === 'polygon' && lens.points && modifiedVertices) {
          const newPoints = lens.points.map((point, idx) => {
            const customVertex = modifiedVertices[`vertex${idx}`];
            return customVertex || point;
          });

          return {
            ...lens,
            points: newPoints
          };
        }

        // Jeśli prostokąt ma zmodyfikowane wierzchołki, zapisz je jako nowe wymiary
        if (lens.shape === 'rectangle' && modifiedVertices) {
          const rectWidth = Math.abs(lens.width);
          const rectHeight = Math.abs(lens.height);
          const rectX = lens.width < 0 ? lens.x + lens.width : lens.x;
          const rectY = lens.height < 0 ? lens.y + lens.height : lens.y;

          const defaultTopLeft = { x: rectX, y: rectY };
          const defaultTopRight = { x: rectX + rectWidth, y: rectY };
          const defaultBottomRight = { x: rectX + rectWidth, y: rectY + rectHeight };
          const defaultBottomLeft = { x: rectX, y: rectY + rectHeight };

          const topLeft = modifiedVertices.topLeft || defaultTopLeft;
          const topRight = modifiedVertices.topRight || defaultTopRight;
          const bottomRight = modifiedVertices.bottomRight || defaultBottomRight;
          const bottomLeft = modifiedVertices.bottomLeft || defaultBottomLeft;

          // Konwertuj prostokąt z przesunniętymi wierzchołkami na wielokąt
          return {
            ...lens,
            shape: 'polygon',
            points: [topLeft, topRight, bottomRight, bottomLeft],
            // Usuń stare właściwości prostokąta
            x: undefined,
            y: undefined,
            width: undefined,
            height: undefined
          };
        }

        return lens;
      });
    });

    // Wyczyść tymczasowe stany edycji
    setLensVertices({});
    setLensControlPoints({});

    // Pokaż komunikat
    alert('Zmiany zostały zapisane! ✓');
  };

  return {
    // States
    lensMode, setLensMode,
    lensShape, setLensShape,
    currentLens, setCurrentLens,
    editingLens, setEditingLens,
    draggingLens, setDraggingLens,
    resizingLens, setResizingLens,
    lensPolygonPoints, setLensPolygonPoints,
    lensEditMode, setLensEditMode,
    lensMoveMode, setLensMoveMode,
    lensControlPoints, setLensControlPoints,
    draggingLensControl, setDraggingLensControl,
    lensVertices, setLensVertices,
    draggingLensVertex, setDraggingLensVertex,

    // Functions
    handleLensStart,
    handleLensMove,
    handleLensEnd,
    handleLensPolygonComplete,
    openLensPropertiesDialog,
    saveLensProperties,
    deleteLens,
    handleLensDoubleClick,
    handleLensControlMouseDown,
    handleLensControlMouseMove,
    handleLensControlMouseUp,
    resetLensControlPoint,
    handleLensVertexMouseDown,
    handleLensVertexMouseMove,
    handleLensVertexMouseUp,
    resetLensVertex,
    handleLensDragStart,
    handleLensDragMove,
    handleLensDragEnd,
    saveLensEdits,
    renderLens,
  };
}
